import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaRegClock, FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaQrcode } from 'react-icons/fa';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';

import { apiService } from '../../components/Config/api.service';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import {
  ancreVoisine, dateHeureLocale, heureLocale, jourLisible, periodeDe,
} from '../Presence/formatPointages';

/**
 * Pointage du salarié (contrat C11, Yros/Kavero#59).
 *
 * Le bouton est **neutre** : l'écran ne peut pas savoir si le prochain
 * pointage est une entrée ou une sortie — l'état de présence du jour est
 * refusé à l'employé, et aucun contrat ne l'expose. C'est `sens`, dans la
 * réponse, qui le nomme après coup.
 *
 * Pas de second `HeaderCard` : la coquille en pose déjà un. Message d'état
 * inline sous le bouton, comme sur `DemandeConge` — le contrat exige
 * l'explication « à l'endroit du bouton », un toast n'y est pas.
 *
 * Depuis le lot A de la recette (`Yros/Kavero#369`), C14
 * (`GET /api/rh/mes-pointages`) comble le trou décrit ci-dessus : le bouton
 * nomme l'action qu'il déclenchera, l'état du jour survit à un rechargement,
 * et la semaine écoulée est lisible. Le message de confirmation, lui, reste
 * muet sur le contrôle de zone — c'est l'historique qui le porte, là où le
 * salarié le consulte sans être en train d'agir.
 *
 * Pointage par QR code (C11 étendu, Yros/Kavero#459) : l'affiche d'un lieu
 * encode `/espace/pointage?lieu=<jeton>`, que l'appareil photo du téléphone
 * ouvre. Le jeton part avec le pointage, la position aussi si elle vient,
 * mais elle ne bloque plus rien. Rien n'est envoyé avant le clic : ouvrir un
 * lien ne doit pas pointer. Sans `lieu`, l'écran est celui d'avant.
 *
 * Note de fin de journée (C11, C14 et C26 étendus, Yros/Kavero#464) : au
 * départ, le salarié peut dire en quelques mots ce qu'il a fait. Elle ne
 * bloque jamais le pointage, et ne change ni le contrôle de zone ni le sens.
 * Il la relit dans « Mes pointages », où il peut l'écrire ou la corriger tant
 * que la semaine n'est pas envoyée à la RH ; ensuite elle est figée, jusqu'à ce
 * que la RH renvoie la semaine.
 */

const DELAI_POSITION_MS = 8000;

// La note de fin de journée tient en 500 caractères au plus (C11 étendu). Le
// champ s'arrête là : un 400 du proxy sur une note trop longue tomberait en
// « erreur technique » et bloquerait le pointage.
const LONGUEUR_NOTE = 500;
const CLASSE_CHAMP_NOTE = 'mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[16px] text-slate-900 placeholder:text-slate-500 focus:border-[color:var(--color-primary)] focus:outline focus:outline-2 focus:outline-[color:var(--color-primary)] disabled:opacity-60';

// Les deux coordonnées ensemble, ou aucune. Une demi-position ferait un 400 :
// C11 exige l'égalité stricte des clés du corps.
const POSITION_INCONNUE = { latitude: null, longitude: null };

// Borne de `jeton_lieu` dans C11 étendu. Au-delà, le proxy répondrait 400 —
// une erreur technique pour ce qui est une affiche illisible.
const JETON_LIEU_LONGUEUR_MAX = 128;

// `motif` existe pour que le portail choisisse son message sans lire le texte.
// Ces libellés ne servent que l'enveloppe dégradée — dès qu'Odoo porte son
// message, c'est lui qui parle.
const REPLI_PAR_MOTIF = {
  hors_zone: 'pointage.refusHorsZone',
  non_verifiable: 'pointage.refusNonVerifiable',
  qr_invalide: 'pointage.refusQrInvalide',
};

/**
 * Ne rejette jamais : résout toujours les deux coordonnées, ou les deux à
 * `null` ensemble. Permission déclinée, position indisponible, délai écoulé,
 * `geolocation` absent, coordonnée non finie — même issue.
 */
const lirePosition = () =>
  new Promise((resoudre) => {
    const geolocation = navigator.geolocation;
    if (!geolocation) {
      resoudre(POSITION_INCONNUE);
      return;
    }

    let tranche = false;
    const trancher = (position) => {
      if (tranche) return;
      tranche = true;
      resoudre(position);
    };

    // Le délai est tenu deux fois. L'option `timeout` ci-dessous ne court pas
    // tant que l'utilisateur n'a pas répondu à l'invite de permission : une
    // invite laissée ouverte n'est bornée par rien côté navigateur.
    const minuterie = setTimeout(() => trancher(POSITION_INCONNUE), DELAI_POSITION_MS);
    const arreter = (position) => {
      clearTimeout(minuterie);
      trancher(position);
    };

    geolocation.getCurrentPosition(
      (mesure) => {
        const { latitude, longitude } = mesure?.coords || {};
        arreter(
          Number.isFinite(latitude) && Number.isFinite(longitude)
            ? { latitude, longitude }
            : POSITION_INCONNUE
        );
      },
      () => arreter(POSITION_INCONNUE),
      { enableHighAccuracy: true, timeout: DELAI_POSITION_MS, maximumAge: 0 }
    );
  });

/**
 * Le statut d'abord, jamais la présence d'un message : `enrichError` pose un
 * `userMessage` sur TOUTE erreur, et sur un 502 ce serait « Service
 * indisponible » — un texte de proxy, servi au salarié.
 */
const interpreterProbleme = (erreur, t) => {
  const enveloppe = erreur?.response?.status === 409 ? erreur.response.data : null;
  if (!enveloppe) {
    return { motif: 'technique', message: t('pointage.erreurTechnique') };
  }

  const cleDeRepli = REPLI_PAR_MOTIF[enveloppe.motif];
  return {
    // Un motif hors contrat ne se replie pas sur « hors zone » : accuser le
    // salarié d'être ailleurs quand on n'en sait rien est précisément ce que
    // les deux motifs distincts de C11 servent à éviter.
    motif: enveloppe.motif || 'technique',
    message: enveloppe.message || (cleDeRepli ? t(cleDeRepli) : t('pointage.erreurTechnique')),
  };
};

// Refus de C26 (écrire la note d'un pointage passé). Le message d'Odoo prime ;
// ces libellés ne servent que si l'enveloppe n'en porte pas.
const REPLI_NOTE_PAR_MOTIF = {
  pointage_inconnu: 'pointage.noteRefusPointageInconnu',
  semaine_verrouillee: 'pointage.noteRefusSemaineVerrouillee',
  note_invalide: 'pointage.noteRefusNoteInvalide',
};

// Le statut d'abord, jamais la présence d'un message : un 500 applicatif dont le
// corps porte `message` serait sinon servi mot pour mot au salarié.
const interpreterRefusNote = (erreur, t) => {
  const enveloppe = erreur?.response?.status === 409 ? erreur.response.data : null;
  const cleDeRepli = enveloppe && REPLI_NOTE_PAR_MOTIF[enveloppe.motif];
  return (enveloppe && enveloppe.message)
    || (cleDeRepli ? t(cleDeRepli) : t('pointage.noteErreurTechnique'));
};

// Seuls les deux états en défaut portent un libellé : `dans_zone` n'a rien à
// dire, et l'afficher noierait les deux qui comptent.
// Refus de C15. Comme pour C11, le message d'Odoo prime ; ces libellés ne
// servent que si l'enveloppe n'en porte pas.
const REPLI_ANNULATION_PAR_MOTIF = {
  aucun_pointage: 'pointage.refusAucunPointage',
  trop_ancien: 'pointage.refusTropAncien',
  mois_verrouille: 'pointage.refusMoisVerrouille',
  sans_fiche: 'pointage.refusSansFiche',
};

const departsDe = (jour) => jour.pointages.filter((pointage) => pointage.sortie).length;

const MARQUEURS = {
  hors_zone: 'pointage.marqueurHorsZone',
  non_verifiable: 'pointage.marqueurNonVerifiable',
};

/**
 * La note de fin de journée d'un départ passé, dans « Mes pointages » (C14
 * étendu et C26, Yros/Kavero#464) : le salarié la relit, avec la date de sa
 * dernière modification, et peut l'écrire ou la corriger par un éditeur en
 * ligne tant que la semaine n'est pas envoyée à la RH (`note_modifiable`).
 * L'éditeur est propre à ce pointage ; `onEnregistree` relit ce que l'écran
 * affiche.
 */

/** Une icône ⓘ qui montre `texte` au survol, au focus ou au toucher. */
function InfoBulle({ texte, libelle }) {
  const [ouverte, setOuverte] = useState(false);
  const id = useId();
  return (
    <span className="relative inline-flex group">
      <button
        type="button"
        data-testid="pointage-aide"
        aria-label={libelle}
        aria-describedby={id}
        aria-expanded={ouverte}
        onClick={() => setOuverte((v) => !v)}
        onBlur={() => setOuverte(false)}
        className="p-1 rounded-full text-slate-400 hover:text-[--color-primary] focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
      >
        <FaInfoCircle className="w-4 h-4" aria-hidden="true" />
      </button>
      <span
        id={id}
        role="tooltip"
        data-testid="pointage-aide-texte"
        className={`absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-lg bg-slate-800 px-3 py-2 text-xs leading-relaxed text-white shadow-lg
          ${ouverte ? 'block' : 'hidden group-hover:block group-focus-within:block'}`}
      >
        {texte}
      </span>
    </span>
  );
}

function NoteDuPointage({ pointage, fuseau, langue, repere, onEnregistree }) {
  const { t } = useTranslation();
  const idEditeur = useId();
  const champ = useRef(null);
  const declencheur = useRef(null);
  const [ouvert, setOuvert] = useState(false);
  const [brouillon, setBrouillon] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (ouvert) champ.current?.focus();
  }, [ouvert]);

  // Une note s'écrit au départ : une arrivée seule n'en porte pas, même si le
  // champ en garde une (une correction de la RH peut rouvrir la ligne).
  if (!pointage.sortie) return null;

  const modifiable = pointage.note_modifiable === true;
  // Semaine envoyée à la RH : la note se lit, elle ne se change plus.
  const figee = pointage.note_modifiable === false;
  if (!pointage.note && !modifiable && !figee) return null;

  const modifieeLe = dateHeureLocale(pointage.note_modifiee_le, fuseau, langue);

  const ouvrir = () => {
    setBrouillon(pointage.note || '');
    setErreur(null);
    setOuvert(true);
  };

  const refermer = () => {
    setOuvert(false);
    declencheur.current?.focus();
  };

  const fermer = () => {
    if (!enCours) refermer();
  };

  const enregistrer = async () => {
    if (enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      // Une note vide ou faite d'espaces s'envoie `null` : c'est ainsi qu'on
      // l'efface (C26).
      await apiService.ecrireNoteSortie({ pointage_id: pointage.id, note: brouillon.trim() || null });
      await onEnregistree();
      refermer();
    } catch (probleme) {
      // L'éditeur reste ouvert avec ce que le salarié a tapé : il corrige ou
      // renonce, rien n'est perdu.
      setErreur(interpreterRefusNote(probleme, t));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div data-testid={`note-sortie-bloc-${pointage.id}`} className="mt-1.5 space-y-1 text-sm">
      {repere && <p className="text-xs font-medium text-slate-700">{repere}</p>}

      {pointage.note && (
        <p
          data-testid={`note-sortie-${pointage.id}`}
          className="whitespace-pre-line break-words rounded-lg bg-slate-50 px-3 py-2 text-slate-700"
        >
          <span className="font-medium text-slate-800">{t('pointage.noteMaNote')}</span>
          {' '}
          {pointage.note}
        </p>
      )}

      {(modifieeLe || modifiable || figee) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {modifieeLe && (
            <span data-testid={`note-sortie-modifiee-${pointage.id}`} className="text-xs text-slate-500">
              {t('pointage.noteModifieeLe', modifieeLe)}
            </span>
          )}
          {modifiable && (
            <button
              ref={declencheur}
              type="button"
              data-testid={`note-sortie-ouvrir-${pointage.id}`}
              aria-expanded={ouvert}
              aria-controls={ouvert ? idEditeur : undefined}
              onClick={ouvert ? fermer : ouvrir}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              {t(pointage.note ? 'pointage.noteModifier' : 'pointage.noteAjouter')}
            </button>
          )}
          {figee && (
            <span data-testid={`note-sortie-figee-${pointage.id}`} className="text-xs text-slate-500">
              {t('pointage.noteFigee')}
            </span>
          )}
        </div>
      )}

      {ouvert && (
        <div
          id={idEditeur}
          onKeyDown={(evenement) => {
            if (evenement.key === 'Escape') fermer();
          }}
          className="rounded-xl border border-slate-200 bg-white p-3"
        >
          <label htmlFor={`${idEditeur}-champ`} className="block text-xs font-medium text-slate-700">
            {t('pointage.noteLibelle')}
          </label>
          <textarea
            ref={champ}
            id={`${idEditeur}-champ`}
            data-testid={`note-sortie-champ-${pointage.id}`}
            rows={3}
            maxLength={LONGUEUR_NOTE}
            value={brouillon}
            onChange={(evenement) => setBrouillon(evenement.target.value)}
            disabled={enCours}
            aria-describedby={`${idEditeur}-compteur`}
            className={CLASSE_CHAMP_NOTE}
          />
          <p
            id={`${idEditeur}-compteur`}
            data-testid={`note-sortie-compteur-${pointage.id}`}
            className="mt-1 text-xs text-slate-500"
          >
            {t('pointage.noteRestants', { count: LONGUEUR_NOTE - brouillon.length })}
          </p>
          {erreur && (
            <p
              data-testid={`note-sortie-erreur-${pointage.id}`}
              role="alert"
              className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {erreur}
            </p>
          )}
          <div className="mt-2 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              data-testid={`note-sortie-annuler-${pointage.id}`}
              onClick={fermer}
              disabled={enCours}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              {t('pointage.noteAnnuler')}
            </button>
            <button
              type="button"
              data-testid={`note-sortie-enregistrer-${pointage.id}`}
              onClick={enregistrer}
              disabled={enCours}
              className="rounded-lg bg-[--color-primary] px-4 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-60"
            >
              {enCours ? t('pointage.noteEnregistrement') : t('pointage.noteEnregistrer')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Pointage() {
  const { t, i18n } = useTranslation();
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [sens, setSens] = useState(null);
  // Le jeton du QR scanné, tant que le pointage qu'il annonce n'est pas
  // terminé. Un `lieu` vide n'est pas un QR : l'écran reste celui d'avant.
  const [parametres, setParametres] = useSearchParams();
  const jetonLieu = parametres.get('lieu') || null;
  const [succesQr, setSuccesQr] = useState(null);
  const [probleme, setProbleme] = useState(null);
  const [etat, setEtat] = useState(null);
  const [etatIndisponible, setEtatIndisponible] = useState(false);
  // Confirmation en deux temps : sans elle, le bouton d'annulation devient à
  // son tour une source de clics accidentels — exactement ce qu'il répare.
  const [confirmation, setConfirmation] = useState(false);
  const [annulationEnCours, setAnnulationEnCours] = useState(false);
  const [annule, setAnnule] = useState(false);
  // La note de fin de journée, saisie avant de pointer son départ.
  const [note, setNote] = useState('');
  // Une note envoyée que la réponse ne confirme pas : le pointage est enregistré,
  // la note non. C'est un avis, pas un refus.
  const [noteNonEnregistree, setNoteNonEnregistree] = useState(false);
  const idNote = useId();
  // Séquencement des lectures : la réponse d'un appel supplanté est jetée.
  // C'est une lecture, donc pas de verrou — un double appel coûte un
  // aller-retour, alors qu'un verrou figerait l'écran si l'un ne revenait
  // jamais (patron du bolt 032, `PresenceDuJour`).
  const dernierAppel = useRef(0);
  // « Mes pointages » : la période que le salarié a choisie. Tant qu'elle est
  // `null`, la fenêtre par défaut (`etat`) tient lieu d'historique ; `etat`
  // reste la seule source du bouton et de l'état du jour, que naviguer dans
  // l'historique ne doit jamais changer.
  const [periode, setPeriode] = useState(null);
  const [historique, setHistorique] = useState(null);
  const [erreurHistorique, setErreurHistorique] = useState(null);
  const periodeRef = useRef(null);
  const dernierHistorique = useRef(0);

  const rafraichirEtat = useCallback(async () => {
    const appelActuel = ++dernierAppel.current;
    try {
      const reponse = await apiService.getMesPointages();
      if (appelActuel !== dernierAppel.current) return;
      setEtat(reponse);
      setEtatIndisponible(false);
    } catch (erreur) {
      if (appelActuel !== dernierAppel.current) return;
      signalerErreur(erreur, 'Pointage.rafraichirEtat');
      // L'écran ne se ferme pas sur un échec de lecture : pointer reste
      // possible, c'est seulement le libellé du bouton qui redevient neutre.
      setEtat(null);
      setEtatIndisponible(true);
    }
  }, []);

  useEffect(() => {
    rafraichirEtat();
  }, [rafraichirEtat]);

  // Même séquencement que `rafraichirEtat` : seule la dernière lecture compte.
  // Un échec garde la période déjà lue — la perdre serait pire que ne pas
  // avoir changé de période.
  const chargerHistorique = useCallback(async (choisie) => {
    const appelActuel = ++dernierHistorique.current;
    const { du, au } = periodeDe(choisie.type, choisie.ancre, choisie.aujourdhui);
    try {
      const reponse = await apiService.getMesPointages(du, au);
      if (appelActuel !== dernierHistorique.current) return;
      setHistorique(reponse);
      setErreurHistorique(null);
    } catch (erreur) {
      if (appelActuel !== dernierHistorique.current) return;
      const enveloppe = erreur?.response?.status === 409 ? erreur.response.data : null;
      setErreurHistorique(enveloppe?.message || t('pointage.erreurHistorique'));
    }
  }, [t]);

  const choisirPeriode = (suivante) => {
    periodeRef.current = suivante;
    setPeriode(suivante);
    chargerHistorique(suivante);
  };

  // Le dernier jour de la fenêtre par défaut est aujourd'hui, dans le fuseau
  // du salarié : c'est Odoo qui le dit, jamais le navigateur.
  const aujourdhui = etat?.ok ? etat.jours[etat.jours.length - 1]?.date : null;
  const typePeriode = periode?.type || 'semaine';

  const changerType = (type) => {
    if (!aujourdhui || type === typePeriode) return;
    choisirPeriode({ type, ancre: aujourdhui, aujourdhui });
  };

  const naviguer = (pas) => {
    if (!aujourdhui) return;
    const courante = periode || { type: 'semaine', ancre: aujourdhui, aujourdhui };
    choisirPeriode({ ...courante, ancre: ancreVoisine(courante.type, courante.ancre, pas) });
  };

  const suivantPossible = Boolean(periode)
    && periodeDe(periode.type, periode.ancre, periode.aujourdhui).au < periode.aujourdhui;

  // Une période affichée doit suivre ce qui vient d'être posé ou annulé.
  const relirePeriode = async () => {
    if (periodeRef.current) await chargerHistorique(periodeRef.current);
  };

  // Un pointage terminé — accepté ou refusé — consomme le lien : sans cela, un
  // rechargement reproposerait de pointer par QR. `replace`, pour que le
  // bouton Retour ne ramène pas non plus sur le lien.
  const retirerLieu = () => {
    setParametres((courants) => {
      const suivants = new URLSearchParams(courants);
      suivants.delete('lieu');
      return suivants;
    }, { replace: true });
  };

  // Le champ de note n'existe qu'au départ.
  const departPropose = Boolean(etat?.ok && etat.pointe);

  // Une note écrite change ce que « Mes pointages » affiche : on relit la
  // fenêtre par défaut, puis la période choisie, comme après un pointage.
  const apresNoteEcrite = async () => {
    await rafraichirEtat();
    await relirePeriode();
  };

  const pointer = async () => {
    // Garde dans le handler, pas seulement le `disabled` du bouton : un double
    // pointage, c'est une entrée suivie d'une sortie immédiate dans
    // `hr.attendance`.
    if (envoiEnCours) return;

    // La note se lit au clic, avant l'attente de la position (jusqu'à 8 s). Une
    // note faite d'espaces vaut absente, et une note n'accompagne jamais une
    // arrivée : dans les deux cas la clé n'existe pas dans le corps (C11 étendu).
    const texte = departPropose ? note.trim() : '';

    setSens(null);
    setSuccesQr(null);
    setNoteNonEnregistree(false);
    setProbleme(null);

    if (jetonLieu && jetonLieu.length > JETON_LIEU_LONGUEUR_MAX) {
      setProbleme({ motif: 'qr_invalide', message: t('pointage.refusQrInvalide') });
      retirerLieu();
      return;
    }

    setEnvoiEnCours(true);

    try {
      const position = await lirePosition();
      // Deux clés facultatives de C11 étendu, indépendantes l'une de l'autre :
      // le jeton du QR scanné (Yros/Kavero#459) et la note de fin de journée
      // (Yros/Kavero#464). Un départ par QR peut porter les deux.
      const corps = { ...position };
      if (jetonLieu) corps.jeton_lieu = jetonLieu;
      if (texte) corps.note = texte;
      const reponse = await apiService.poserPointage(corps);
      // `controle_zone` n'est pas retenu : le marqueur sert la RH, et signaler
      // au salarié un pointage accepté « mais hors zone » l'inquiéterait pour
      // une décision qui n'est pas la sienne.
      const sensRendu = reponse?.sens === 'sortie' ? 'sortie' : 'entree';
      setSens(sensRendu);
      // Odoo ne retient la note que sur un départ et la rend dans la réponse :
      // une arrivée, ou une réponse qui ne la rend pas, ne confirme rien.
      setNoteNonEnregistree(Boolean(texte) && !(sensRendu === 'sortie' && reponse?.note));
      setAnnule(false);
      if (jetonLieu) {
        // Le nom du lieu ne vient que d'ici : aucune route ne résout le jeton
        // avant le clic.
        setSuccesQr({
          heure: heureLocale(reponse?.horodatage, etat?.fuseau, i18n?.language),
          lieu: reponse?.lieu || null,
        });
        retirerLieu();
      }
      // La note est partie : le prochain départ repart d'un champ vide. Après un
      // refus, elle reste, pour que le salarié repointe sans tout réécrire.
      setNote('');
      // L'état relu est la preuve durable : le message de confirmation
      // disparaît au rechargement, pas lui.
      await rafraichirEtat();
      await relirePeriode();
    } catch (erreur) {
      const refus = interpreterProbleme(erreur, t);
      setProbleme(refus);
      // Une panne technique garde le lien : le salarié doit pouvoir réessayer
      // sans rescanner l'affiche.
      if (jetonLieu && refus.motif !== 'technique') retirerLieu();
    } finally {
      setEnvoiEnCours(false);
    }
  };

  const annuler = async () => {
    if (annulationEnCours) return;
    setAnnulationEnCours(true);
    setProbleme(null);
    try {
      const reponse = await apiService.annulerDernierPointage();
      setEtat(reponse);
      setEtatIndisponible(false);
      // La confirmation du pointage précédent devient fausse dès qu'il est
      // annulé : la laisser afficherait « Entrée enregistrée » sous un
      // historique où elle n'est plus.
      setSens(null);
      setNoteNonEnregistree(false);
      setAnnule(true);
      setConfirmation(false);
      await relirePeriode();
    } catch (erreur) {
      const enveloppe = erreur?.response?.status === 409 ? erreur.response.data : null;
      const cleDeRepli = enveloppe && REPLI_ANNULATION_PAR_MOTIF[enveloppe.motif];
      setProbleme({
        motif: (enveloppe && enveloppe.motif) || 'technique',
        message: (enveloppe && enveloppe.message)
          || (cleDeRepli ? t(cleDeRepli) : t('pointage.erreurTechnique')),
      });
      setConfirmation(false);
      // L'état est relu : un refus `trop_ancien` peut venir d'un écran qui
      // affichait une journée périmée, et le rafraîchir remet les deux
      // d'accord.
      await rafraichirEtat();
    } finally {
      setAnnulationEnCours(false);
    }
  };

  const langue = i18n?.language;
  // La période choisie, ou la fenêtre par défaut tant qu'aucune ne l'est.
  const affiche = historique || etat;
  const fuseau = affiche?.fuseau || etat?.fuseau;
  const joursSansPointage = affiche?.jours_sans_pointage || [];
  // La note n'apparaît que si un repère est réellement affiché. Posée en
  // permanence, elle expliquerait un signal absent et deviendrait du bruit.
  const marqueursPresents = (affiche?.jours || []).some((jour) =>
    jour.pointages.some(
      (pointage) => MARQUEURS[pointage.controle_entree] || MARQUEURS[pointage.controle_sortie]
    )
  );
  const depuis = heureLocale(etat?.depuis, etat?.fuseau, langue);
  // Libellé neutre tant que l'état n'est pas connu : annoncer « pointer mon
  // départ » à quelqu'un qui n'est pas entré est pire que ne rien annoncer.
  // Le bouton n'apparaît que s'il y a quelque chose à annuler AUJOURD'HUI —
  // même condition que la garde d'Odoo, pour ne jamais proposer une action
  // qui sera refusée. Le dernier jour de la fenêtre est aujourd'hui.
  const jourCourant = etat?.ok ? etat.jours[etat.jours.length - 1] : null;
  const annulationPossible = Boolean(jourCourant && jourCourant.pointages.length > 0);
  const libelleSelonEtat = !etat?.ok
    ? t('pointage.bouton')
    : t(etat.pointe ? 'pointage.boutonSortie' : 'pointage.boutonEntree');
  const libelleBouton = jetonLieu ? t('pointage.boutonQr') : libelleSelonEtat;

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
      <div className="flex items-center gap-3">
        <FaRegClock className="w-5 h-5 shrink-0 text-[--color-primary]" aria-hidden="true" />
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-800">
          {t('pointage.titre')}
        </h2>
        {/* Yros/Kavero#491 : l'explication au survol, ou au toucher sur
            téléphone, plutôt qu'un paragraphe qui repousse le bouton. */}
        <InfoBulle texte={t('pointage.description')} libelle={t('pointage.aide')} />
      </div>
      {jetonLieu && (
        <p data-testid="pointage-qr-consigne" className="mt-2 text-sm font-medium text-slate-700">
          {t('pointage.consigneQr')}
        </p>
      )}

      {/* Au départ seulement : à l'arrivée, ou quand l'état du jour est
          inconnu, le champ gênerait celui qui veut pointer vite. */}
      {departPropose && (
        <div className="mt-4">
          <label htmlFor={idNote} className="block text-sm font-medium text-slate-700">
            {t('pointage.noteLibelle')}
          </label>
          <textarea
            id={idNote}
            data-testid="pointage-note-depart"
            rows={3}
            maxLength={LONGUEUR_NOTE}
            value={note}
            onChange={(evenement) => setNote(evenement.target.value)}
            disabled={envoiEnCours}
            placeholder={t('pointage.notePlaceholder')}
            aria-describedby={`${idNote}-compteur`}
            className={CLASSE_CHAMP_NOTE}
          />
          <p
            id={`${idNote}-compteur`}
            data-testid="pointage-note-depart-compteur"
            className="mt-1 text-xs text-slate-500"
          >
            {t('pointage.noteRestants', { count: LONGUEUR_NOTE - note.length })}
          </p>
        </div>
      )}

      <button
        type="button"
        data-testid="pointage-bouton"
        onClick={pointer}
        disabled={envoiEnCours}
        className="mt-5 w-full sm:w-auto bg-[--color-primary] text-white hover:opacity-90 active:scale-95 disabled:opacity-60 disabled:active:scale-100 transition-all duration-200 shadow-sm rounded-lg px-5 py-2.5 text-sm font-medium"
      >
        {envoiEnCours ? t('pointage.envoi') : libelleBouton}
      </button>

      {annulationPossible && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {!confirmation ? (
            <button
              type="button"
              data-testid="pointage-annuler"
              onClick={() => setConfirmation(true)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              {t('pointage.annuler')}
            </button>
          ) : (
            <>
              <button
                type="button"
                data-testid="pointage-annuler-confirmer"
                onClick={annuler}
                disabled={annulationEnCours}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {annulationEnCours ? t('pointage.annulerEnCours') : t('pointage.annulerConfirmer')}
              </button>
              <button
                type="button"
                data-testid="pointage-annuler-renoncer"
                onClick={() => setConfirmation(false)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-600 hover:bg-slate-50"
              >
                {t('pointage.annulerRenoncer')}
              </button>
            </>
          )}
        </div>
      )}

      {annule && (
        <p data-testid="pointage-annule" className="mt-3 text-sm text-slate-600">
          {t('pointage.annulerSucces')}
        </p>
      )}

      {etat?.ok && (
        <p data-testid="pointage-etat-du-jour" className="mt-3 text-sm text-slate-600">
          {etat.pointe && depuis
            ? t('pointage.pointeDepuis', { heure: depuis })
            : t('pointage.pasPointe')}
        </p>
      )}

      {etatIndisponible && (
        <p data-testid="pointage-etat-indisponible" className="mt-3 text-sm text-slate-500">
          {t('pointage.etatIndisponible')}
        </p>
      )}

      {sens && (
        <div
          data-testid="pointage-succes"
          className="mt-4 flex items-start gap-2.5 rounded-xl border border-green-200 bg-green-100 px-4 py-3 text-sm font-medium text-green-800"
        >
          <FaCheckCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            {t(sens === 'sortie' ? 'pointage.succesSortie' : 'pointage.succesEntree')}
            {succesQr?.lieu && (
              <span data-testid="pointage-succes-qr" className="block font-normal">
                {succesQr.heure
                  ? t('pointage.succesQr', { heure: succesQr.heure, lieu: succesQr.lieu })
                  : succesQr.lieu}
              </span>
            )}
          </span>
        </div>
      )}

      {noteNonEnregistree && (
        <div
          data-testid="pointage-note-non-enregistree"
          role="status"
          className="mt-3 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <FaInfoCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{t('pointage.noteNonEnregistree')}</span>
        </div>
      )}

      {affiche?.ok && (
        <section data-testid="pointage-semaine" className="mt-6 border-t border-slate-100 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-800">{t('pointage.mesPointages')}</h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex overflow-hidden rounded-lg border border-slate-200 text-xs font-medium">
                {['semaine', 'mois'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    data-testid={`pointage-periode-${type}`}
                    onClick={() => changerType(type)}
                    disabled={!aujourdhui}
                    aria-pressed={typePeriode === type}
                    className={`px-3 py-1.5 ${typePeriode === type
                      ? 'bg-[--color-primary] text-white' : 'text-slate-700 hover:bg-slate-50'}`}
                  >
                    {t(`pointage.${type}`)}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid="pointage-periode-precedent"
                  onClick={() => naviguer(-1)}
                  disabled={!aujourdhui}
                  aria-label={t('pointage.periodePrecedente')}
                  className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  <HiChevronLeft className="w-4 h-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  data-testid="pointage-periode-suivant"
                  onClick={() => naviguer(1)}
                  disabled={!suivantPossible}
                  aria-label={t('pointage.periodeSuivante')}
                  className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  <HiChevronRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {erreurHistorique && (
            <p
              data-testid="pointage-historique-erreur"
              className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {erreurHistorique}
            </p>
          )}

          <ul className="mt-3 space-y-2">
            {affiche.jours.map((jour) => (
              <li
                key={jour.date}
                data-testid={`pointage-jour-${jour.date}`}
                className="rounded-xl border border-slate-100 px-3 py-2"
              >
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm font-medium capitalize text-slate-700">
                    {jourLisible(jour.date, langue)}
                  </span>

                  {jour.pointages.length === 0 ? (
                    <span className="flex flex-wrap items-center gap-2 text-sm text-slate-400">
                      {t('pointage.jourSansPointage')}
                      {joursSansPointage.includes(jour.date) && (
                        <span
                          data-testid={`pointage-attendu-sans-pointage-${jour.date}`}
                          className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                        >
                          {t('pointage.attenduSansPointage')}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
                      {jour.pointages.map((pointage, rang) => {
                        const anomalies = pointage.anomalies || [];
                        const sortieManquante = anomalies.includes('sortie_manquante');
                        return (
                          <span key={`${jour.date}-${rang}`} className="flex flex-wrap items-center gap-2">
                            <span>
                              {t('pointage.entree')} {heureLocale(pointage.entree, fuseau, langue)}
                              {' · '}
                              {t('pointage.sortie')}{' '}
                              {heureLocale(pointage.sortie, fuseau, langue)
                                || (sortieManquante ? '—' : t('pointage.enCours'))}
                            </span>
                            {/* Une seule icône par pointage, que le QR ait servi
                                à l'arrivée, au départ ou aux deux. Aucune
                                distance ici : elle ne se montre qu'à la RH. */}
                            {[pointage.methode_entree, pointage.methode_sortie].includes('qr') && (
                              <span
                                data-testid={`pointage-qr-${pointage.id}`}
                                role="img"
                                aria-label={t('pointage.parQr')}
                                title={t('pointage.parQr')}
                                className="inline-flex items-center text-slate-500"
                              >
                                <FaQrcode className="w-3.5 h-3.5" aria-hidden="true" />
                              </span>
                            )}
                            {[pointage.controle_entree, pointage.controle_sortie]
                              .filter((controle) => MARQUEURS[controle])
                              .map((controle) => (
                                <span
                                  key={controle}
                                  data-testid={`pointage-marqueur-${controle}`}
                                  className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                                >
                                  {t(MARQUEURS[controle])}
                                </span>
                              ))}
                            {sortieManquante && (
                              <span
                                data-testid="pointage-anomalie-sortie_manquante"
                                className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                              >
                                {t('pointage.anomalies.sortie_manquante')}
                              </span>
                            )}
                            {pointage.source === 'correction_rh' && (
                              <span
                                data-testid="pointage-source-correction_rh"
                                className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                              >
                                {t('pointage.sourceCorrectionRh')}
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </span>
                  )}
                </div>

                {jour.pointages.map((pointage, rang) => (
                  <NoteDuPointage
                    key={pointage.id ?? `${jour.date}-${rang}`}
                    pointage={pointage}
                    fuseau={fuseau}
                    langue={langue}
                    // Un jour à deux départs : l'heure dit de quelle sortie il s'agit.
                    repere={pointage.sortie && departsDe(jour) > 1
                      ? `${t('pointage.sortie')} ${heureLocale(pointage.sortie, fuseau, langue)}`
                      : null}
                    onEnregistree={apresNoteEcrite}
                  />
                ))}
              </li>
            ))}
          </ul>

          {/* Le salarié ne corrige pas lui-même : un pointage faux ou manquant
              se signale à la RH, qui le corrige avec une trace (Yros/Kavero#452). */}
          <p data-testid="pointage-signaler-erreur" className="mt-3 text-xs text-slate-500">
            {t('pointage.signalerErreur')}
          </p>

      {/* Un badge nu se lit comme une sanction. Le salarié doit savoir
              trois choses d'un coup : son pointage EST enregistré, le repère
              porte sur sa position et pas sur lui, et il a un recours. C'est
              la réponse à l'objection de Yros/Kavero#353 — expliquer plutôt
              que masquer. */}
          {marqueursPresents && (
            <p data-testid="pointage-note-marqueurs" className="mt-3 text-xs text-slate-500">
              {t('pointage.noteMarqueurs')}
            </p>
          )}
        </section>
      )}

      {probleme && (
        <div
          data-testid="pointage-probleme"
          data-motif={probleme.motif}
          className="mt-4 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
        >
          <FaExclamationTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          {/* Message d'Odoo affiché mot pour mot : le portail ne reformule pas
              le vocabulaire métier, il l'affiche. */}
          <span>{probleme.message}</span>
        </div>
      )}
    </div>
  );
}

export default Pointage;
